import { SQUAD_DEFAULT_ISIN, SQUAD_FONDSDATEN_KEYS, SQUAD_WEITERE_DATEN_KEYS } from './templateConstants';
import { mergeSquadSectionHeaders } from './sectionHeaderPlaceholders';
import {
  DEFAULT_ADVISORS,
  normalizeSquadAdvisors,
  SQUAD_ADVISOR_FOOTER_LOGO,
} from './squadAdvisors';
import { normalizeSquadFondspartner } from './squadFondspartner';
import { resolveSquadSidebarFootnotes, SQUAD_SIDEBAR_FOOTNOTE_PAGES, squadSidebarFootnoteKey } from './squadFootnotes';
import { normalizeSquadLabelOverrides } from './squadLabels';
import { normalizeKalenderjahrData } from './kalenderjahr';
import { normalizePerformanceRiskTable } from './performanceRiskTable';
import { normalizeRollingPerformance } from './rollingPerformance';
import { normalizeMifidOverlay } from './squadMifidOverlay';
import { legacySquadPortfolioSections, normalizeSquadPortfolioSections } from './squadPortfolioSections';
import { normalizeSquadPieStyle } from './squadVectorDonut';
import {
  ensureSquadDynamicFondsdaten,
  makeSquadKursNavDynamic,
  makeSquadAnteilsklassenvolumenDynamic,
  makeSquadFondsvolumenDynamic,
  makeSquadWknDynamic,
  type SquadFondsdatenValue,
} from './squadFondsdatenDynamic';
import { squadFundIsinFromAdditional, syncSquadFundIsin } from './squadFundIsin';
import { parseSquadSrriLevel } from './squadSrriLevel';
import { ensureSquadDynamicGebuehren, makeSquadGebuehrDynamic } from './squadGebuehrenDynamic';
import {
  DEFAULT_ALLOKATION_AKTIEN_LEFT,
  DEFAULT_ALLOKATION_AKTIEN_RIGHT,
  DEFAULT_ALLOKATION_EVENTS_LEFT,
  DEFAULT_ALLOKATION_EVENTS_RIGHT,
  DEFAULT_ASSET_ALLOCATION,
  DEFAULT_ASSET_ALLOCATION_NOTE,
  DEFAULT_CHANCEN,
  DEFAULT_MARKTKAPITALISIERUNG,
  DEFAULT_PAGE3_FOOTER_NOTE,
  DEFAULT_RENDITEN_PORTFOLIODATEN,
  DEFAULT_RISIKEN,
  DEFAULT_RISIKEN_NOTE,
  DEFAULT_RISK_INDICATOR_TEXT,
} from './defaultContent';

function toIsoDateOnly(value: unknown): string {
  if (value == null || value === '') return '';
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return '';
    return value.toISOString().slice(0, 10);
  }
  const raw = String(value).trim();
  if (!raw || /^invalid/i.test(raw)) return '';
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  const de = raw.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (de) {
    return `${de[3]}-${de[2].padStart(2, '0')}-${de[1].padStart(2, '0')}`;
  }
  return raw;
}

function deepEqual(a: any, b: any) {
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return a === b;
  }
}

const DEFAULT_FONDSDATEN: Record<string, unknown> = {
  wkn: makeSquadWknDynamic(),
  isin: SQUAD_DEFAULT_ISIN,
  anteilklasse: 'A',
  vertriebsstatus: 'offen',
  fondskategorie: 'Mischfonds',
  mindestkapitalbeteiligungsquote: '51%',
  fondswaehrung: 'EUR',
  mindestzeichnung: 'keine',
  sparplanfaehig: 'ja',
  auflagedatum: '15.04.2005',
  erstausgabepreis: '100,00 EUR',
  kursNav: makeSquadKursNavDynamic(),
  anteilsklassenvolumen: makeSquadAnteilsklassenvolumenDynamic(),
  fondsvolumen: makeSquadFondsvolumenDynamic(),
  ertragsverwendung: 'ausschüttend',
  fondsdomizil: 'Deutschland',
  geschaeftsjahresende: '31. Dezember',
  vertriebszulassung: 'DE, AT, CH',
  fondstyp: 'Lux. Teil II/FCP',
  sfdr: 'Artikel 8+',
};

/**
 * Keys SQUAD moved from "Weitere Daten" (page 2) up into "Fondsdaten" (page 1) in
 * their layout document of 11.09.2026. Documents saved before that carry the values
 * under weitereDaten, so they are lifted across on read — see liftMovedFondsdaten.
 */
const MOVED_TO_FONDSDATEN = ['fondsdomizil', 'vertriebszulassung', 'fondstyp'] as const;

/**
 * Section headers SQUAD renamed, with the wording they replace.
 *
 * A saved document stores its headers, so the new default alone would never reach an
 * existing factsheet. The rename is therefore applied on read — but only while the
 * document still looks un-migrated (see hasUnmigratedRows). Once it has been saved
 * once, the rule retires and a header typed by hand sticks, even if someone types
 * the old wording back.
 */
const RENAMED_SECTION_HEADERS: Array<{ key: string; from: string; to: string }> = [
  { key: 'weitereDaten', from: 'Weitere Daten', to: 'Gebühren' },
];

/** True while the document still carries the moved rows in their old section. */
function hasUnmigratedRows(weitereDaten: Record<string, unknown> | undefined): boolean {
  if (!weitereDaten) return false;
  return MOVED_TO_FONDSDATEN.some((key) => key in weitereDaten);
}

function resolveSectionHeaders(
  stored: Record<string, unknown> | undefined,
  unmigrated: boolean,
): Record<string, unknown> {
  /** Defaults + CellConfig / `{date}` migration for wertentwicklungRisiko / fondsdaten. */
  const headers: Record<string, unknown> = { ...mergeSquadSectionHeaders(stored) };
  if (!unmigrated) return headers;
  RENAMED_SECTION_HEADERS.forEach(({ key, from, to }) => {
    if (headers[key] === from) headers[key] = to;
  });
  return headers;
}

/**
 * Values a saved document still keeps under weitereDaten are used for the page-1
 * table — otherwise every existing factsheet would silently fall back to the
 * defaults above.
 *
 * The lift only applies while the page-1 field is *absent*. An empty string is a
 * deliberate act — the editor writes one when a field is cleared — and must not be
 * refilled from the old section, which the customer can no longer reach through any
 * form. Together with stripMovedFromWeitereDaten the whole migration converges after
 * the first save.
 */
function liftMovedFondsdaten(
  merged: Record<string, unknown>,
  savedFondsdaten: Record<string, unknown> | undefined,
  weitereDaten: Record<string, unknown> | undefined,
): Record<string, unknown> {
  if (!weitereDaten) return merged;
  const lifted = { ...merged };
  MOVED_TO_FONDSDATEN.forEach((key) => {
    const own = savedFondsdaten?.[key];
    if (own !== undefined && own !== null) return;
    const legacy = weitereDaten[key];
    if (legacy !== undefined && legacy !== null && legacy !== '') lifted[key] = legacy;
  });
  return lifted;
}

/**
 * Drops the moved rows from the page-2 section. They are rendered from page 1 now,
 * and the page-2 editor no longer lists them — leaving the copies in place would keep
 * an invisible value around that nothing can edit or delete, and that the lift above
 * would keep resurrecting.
 */
function stripMovedFromWeitereDaten(weitereDaten: Record<string, unknown>): Record<string, unknown> {
  const stripped = { ...weitereDaten };
  MOVED_TO_FONDSDATEN.forEach((key) => {
    delete stripped[key];
  });
  return stripped;
}

/** Page 2, headed "Gebühren" — fees only; the other rows moved to DEFAULT_FONDSDATEN. */
const DEFAULT_WEITERE_DATEN: Record<string, unknown> = {
  ausgabeaufschlag: makeSquadGebuehrDynamic('ausgabeaufschlag'),
  ruecknahmeabschlag: makeSquadGebuehrDynamic('ruecknahmeabschlag'),
  ter: makeSquadGebuehrDynamic('ter'),
  verwaltungsgebuehr: 'aktuell bis zu 1,50 %',
  erfolgsgebuehr: '15 % p.a. (über 5 % p.a. "Hurdle-Rate" mit ewiger Highwatermark)',
};

const DEFAULT_KONTAKT = {
  company: 'SQUAD Fonds',
  address: 'Am Silbermannpark 1a\n86161 Augsburg',
  phone: 'Tel.: 0821 455 420 10',
  email: 'E-Mail: investor@squad-fonds.de',
  website: 'www.squad-fonds.de',
};

export function mapV1FieldsToV2Data(fields: any) {
  const additional = fields?.additional_fields || {};
  const fundIsin = squadFundIsinFromAdditional(additional);
  const fondsdaten: Record<string, SquadFondsdatenValue> = {
    ...ensureSquadDynamicFondsdaten(
      liftMovedFondsdaten(
        { ...DEFAULT_FONDSDATEN, ...(additional?.fondsdaten || {}) },
        additional?.fondsdaten,
        additional?.weitereDaten,
      ),
    ),
    // The table row shows the fund ISIN — see squadFundIsin.
    isin: fundIsin,
  };
  const mapped = {
    title: fields?.title || additional?.title || 'SQUAD Value - A',
    published_at: toIsoDateOnly(fields?.published_at || additional?.published_at || ''),
    anlagestrategie:
      fields?.strategie ||
      additional?.anlagestrategie ||
      'Der SQUAD Value - A investiert in ein ausgewogenes Portfolio aus Value-Aktien, Events und Anleihen. Ziel ist ein langfristiger Vermögenszuwachs bei kontrolliertem Risiko.',
    monatskommentar:
      typeof additional?.monthlyCommentary === 'string'
        ? additional.monthlyCommentary
        : additional?.monthlyCommentary?.left ||
          fields?.disclaimer ||
          '',
    chartFootnote:
      additional?.chartFootnote ||
      'Ab 01.04.2024 Beratungsübernahme durch Discover Capital GmbH. Fondsumbenennung 2023.',
    performanceRiskFootnote: additional?.performanceRiskFootnote || '',
    page1FooterNote:
      additional?.page1FooterNote ||
      'Die Wertentwicklung der Vergangenheit ist kein verlässlicher Indikator für die zukünftige Wertentwicklung. Quelle: Axxion S.A.',
    fondsdaten,
    weitereDaten: ensureSquadDynamicGebuehren(
      stripMovedFromWeitereDaten({
        ...DEFAULT_WEITERE_DATEN,
        ...(additional?.weitereDaten || {}),
      }),
    ),
    fondspartner: normalizeSquadFondspartner(additional?.fondspartner),
    kontakt: { ...DEFAULT_KONTAKT, ...(additional?.kontakt || {}) },
    advisors: normalizeSquadAdvisors(additional?.advisors || DEFAULT_ADVISORS),
    performanceRiskTable: normalizePerformanceRiskTable(additional?.performanceRiskTable),
    allokationAktienLeft: additional?.allokationAktienLeft || DEFAULT_ALLOKATION_AKTIEN_LEFT,
    allokationAktienRight: additional?.allokationAktienRight || DEFAULT_ALLOKATION_AKTIEN_RIGHT,
    allokationEventsLeft: additional?.allokationEventsLeft || DEFAULT_ALLOKATION_EVENTS_LEFT,
    allokationEventsRight: additional?.allokationEventsRight || DEFAULT_ALLOKATION_EVENTS_RIGHT,
    assetAllocation: additional?.assetAllocation || DEFAULT_ASSET_ALLOCATION,
    marktkapitalisierung: additional?.marktkapitalisierung || DEFAULT_MARKTKAPITALISIERUNG,
    renditenPortfoliodaten: additional?.renditenPortfoliodaten || DEFAULT_RENDITEN_PORTFOLIODATEN,
    allocationFootnoteAktien: additional?.allocationFootnoteAktien || 'eigene Berechnungen / Axxion S.A.',
    allocationFootnoteEvents: additional?.allocationFootnoteEvents || 'WA = Wandelanleihe',
    assetAllocationNote: additional?.assetAllocationNote || DEFAULT_ASSET_ALLOCATION_NOTE,
    assetAllocationColors: Array.isArray(additional?.assetAllocationColors)
      ? additional.assetAllocationColors
      : [],
    marktkapitalisierungColors: Array.isArray(additional?.marktkapitalisierungColors)
      ? additional.marktkapitalisierungColors
      : [],
    portfolioBarColor: additional?.portfolioBarColor || '',
    farbthemeFund: additional?.farbthemeFund || '',
    chancen: additional?.chancen || fields?.chancen || DEFAULT_CHANCEN,
    risiken: additional?.risiken || fields?.risiken || DEFAULT_RISIKEN,
    risikenNote: additional?.risikenNote || DEFAULT_RISIKEN_NOTE,
    // The only SRRI default: renderers take this value as it is (an unreadable stored level → 3).
    riskIndicator: parseSquadSrriLevel(additional?.riskIndicator) ?? 3,
    riskIndicatorText: additional?.riskIndicatorText || DEFAULT_RISK_INDICATOR_TEXT,
    kalenderjahrPerformance: normalizeKalenderjahrData(additional?.kalenderjahrPerformance),
    kalenderjahrFootnote: additional?.kalenderjahrFootnote || '',
    rollingPerformance: normalizeRollingPerformance(additional?.rollingPerformance, fundIsin),
    rollingPerformanceFootnote: additional?.rollingPerformanceFootnote || '',
    rollingPerformanceImageUrl: additional?.rollingPerformanceImageUrl || '',
    customPortfolioIsins: Array.isArray(additional?.customPortfolioIsins)
      ? additional.customPortfolioIsins
      : [],
    page3FooterNote: additional?.page3FooterNote || DEFAULT_PAGE3_FOOTER_NOTE,
    ratingsImageUrl: additional?.ratingsImageUrl || '',
    historischeAssetAllokationImageUrl: additional?.historischeAssetAllokationImageUrl || '',
    investmentprozessImageUrl: additional?.investmentprozessImageUrl || '',
    zusammenfassungImageUrl: additional?.zusammenfassungImageUrl || '',
    /** 1280 × 540 logo under the Fondsberater description (page 1). */
    advisorLogoImageUrl:
      additional?.advisorLogoImageUrl !== undefined
        ? additional.advisorLogoImageUrl
        : SQUAD_ADVISOR_FOOTER_LOGO,
    /** 1280 × 540 logo at the foot of the page-2 sidebar — optional, often an ESG mark. */
    fondspartnerLogoImageUrl: additional?.fondspartnerLogoImageUrl || '',
    /** One list per page, free text — see squadFootnotes. */
    ...resolveSquadSidebarFootnotes(additional),
    /** Own wording for the table labels (only overrides) — see squadLabels. */
    fondsdatenLabels: normalizeSquadLabelOverrides(additional?.fondsdatenLabels, SQUAD_FONDSDATEN_KEYS),
    weitereDatenLabels: normalizeSquadLabelOverrides(additional?.weitereDatenLabels, SQUAD_WEITERE_DATEN_KEYS),
    // Page 4
    erlaeuterungen: additional?.erlaeuterungen || fields?.fussnote || '',
    wichtigeHinweise: additional?.wichtigeHinweise || fields?.disclaimer || '',
    // Chart
    historyIsin: fundIsin,
    /**
     * SQUAD has no chart end of its own: the NAV chart ends at Published at like everything
     * else. The field came over from the SHV templates (V1 wertentwicklung_seit_auflage_end_date)
     * and is gone from the form (Vuka, 25.09.2026) — so a value stored earlier is ignored here
     * instead of moving the chart end with nothing left in the form to clear it.
     */
    historyUntil: '',
    historyLegendLabel: additional?.historyLegendLabel || fields?.title || 'SQUAD Value - A',
    sectionHeaders: resolveSectionHeaders(
      additional?.sectionHeaders,
      hasUnmigratedRows(additional?.weitereDaten),
    ),
    // Brand colors (Excel FactSheet-Header — editable)
    brandPrimary: additional?.brandPrimary || '#0C1C3D',
    brandSecondary: additional?.brandSecondary || '#CFB483',
    mifidPerformanceOverlay: normalizeMifidOverlay(additional?.mifidPerformanceOverlay),
    /** Page-2 donuts: vector unless the document switched back to the canvas image. */
    pieChartStyle: normalizeSquadPieStyle(additional?.pieChartStyle),
    /** Custom download / publish basename; supports `{date}` → YYYYMMDD. */
    pdf_download_name: additional?.pdf_download_name || fields?.pdf_download_name || '',
  };
  return {
    ...mapped,
    /**
     * Seite 2 rechts: Bereiche in Excel-Reihenfolge (Snapshot aus dem Portfolio-Upload).
     * Dokumente von vor diesem Feld: dieselben vier Bereiche wie bisher aus den festen Feldern.
     */
    portfolioSections:
      normalizeSquadPortfolioSections(additional?.portfolioSections) ?? legacySquadPortfolioSections(mapped),
  };
}

export function buildCustomizePayload(formData: any, original: any) {
  // Fondsdaten ISIN and rolling bars are saved with the fund ISIN — see squadFundIsin.
  const v2Data = syncSquadFundIsin(formData);
  const payload: Array<{ key: string; value: any }> = [];
  const safeOriginal = original || {};
  const origAdditional = safeOriginal.additional_fields || {};

  if ((safeOriginal.title || '') !== (v2Data?.title || '')) {
    payload.push({ key: 'title', value: v2Data?.title || '' });
  }
  if (v2Data?.published_at && safeOriginal.published_at !== v2Data.published_at) {
    payload.push({ key: 'published_at', value: toIsoDateOnly(v2Data.published_at) });
  }
  if (v2Data?.anlagestrategie != null && safeOriginal.strategie !== v2Data.anlagestrategie) {
    payload.push({ key: 'strategie', value: v2Data.anlagestrategie });
  }
  if (v2Data?.chancen != null && safeOriginal.chancen !== v2Data.chancen) {
    payload.push({ key: 'chancen', value: v2Data.chancen });
  }
  if (v2Data?.risiken != null && safeOriginal.risiken !== v2Data.risiken) {
    payload.push({ key: 'risiken', value: v2Data.risiken });
  }
  if (v2Data?.wichtigeHinweise != null && (safeOriginal.disclaimer || '') !== (v2Data.wichtigeHinweise || '')) {
    payload.push({ key: 'disclaimer', value: v2Data.wichtigeHinweise || '' });
  }
  if (v2Data?.erlaeuterungen != null && (safeOriginal.fussnote || '') !== (v2Data.erlaeuterungen || '')) {
    payload.push({ key: 'fussnote', value: v2Data.erlaeuterungen || '' });
  }
  if (
    v2Data?.historyUntil &&
    !deepEqual(safeOriginal.wertentwicklung_seit_auflage_end_date, v2Data.historyUntil)
  ) {
    payload.push({ key: 'wertentwicklung_seit_auflage_end_date', value: v2Data.historyUntil });
  }

  const additionalKeys = [
    'monthlyCommentary',
    'chartFootnote',
    'performanceRiskFootnote',
    'page1FooterNote',
    'fondsdaten',
    'weitereDaten',
    'fondspartner',
    'kontakt',
    'advisors',
    'performanceRiskTable',
    'allokationAktienLeft',
    'allokationAktienRight',
    'allokationEventsLeft',
    'allokationEventsRight',
    'assetAllocation',
    'marktkapitalisierung',
    'renditenPortfoliodaten',
    'portfolioSections',
    'allocationFootnoteAktien',
    'allocationFootnoteEvents',
    'assetAllocationNote',
    'assetAllocationColors',
    'marktkapitalisierungColors',
    'portfolioBarColor',
    'farbthemeFund',
    'chancen',
    'risiken',
    'risikenNote',
    'riskIndicator',
    'riskIndicatorText',
    'kalenderjahrPerformance',
    'kalenderjahrFootnote',
    'rollingPerformance',
    'rollingPerformanceFootnote',
    'rollingPerformanceImageUrl',
    'customPortfolioIsins',
    'page3FooterNote',
    'ratingsImageUrl',
    'historischeAssetAllokationImageUrl',
    'investmentprozessImageUrl',
    'zusammenfassungImageUrl',
    'advisorLogoImageUrl',
    'fondspartnerLogoImageUrl',
    ...SQUAD_SIDEBAR_FOOTNOTE_PAGES.map(squadSidebarFootnoteKey),
    'fondsdatenLabels',
    'weitereDatenLabels',
    'erlaeuterungen',
    'wichtigeHinweise',
    'historyIsin',
    'historyUntil',
    'historyLegendLabel',
    'sectionHeaders',
    'brandPrimary',
    'brandSecondary',
    'mifidPerformanceOverlay',
    'pieChartStyle',
    'pdf_download_name',
    'title',
  ] as const;

  // Aus den festen Feldern abgeleitete Bereiche nicht speichern — sonst friert das erste
  // Speichern eines Altdokuments Seite 2 ein (auch Demo-Defaults), und spätere Änderungen an
  // den festen Feldern (z. B. per Skript) kämen nicht mehr im PDF an. Gespeichert wird nur ein
  // echter Snapshot oder die Änderung eines schon gespeicherten.
  const sectionsMirrorLegacy =
    origAdditional.portfolioSections === undefined &&
    deepEqual(v2Data?.portfolioSections, legacySquadPortfolioSections(v2Data || {}));

  const updatedAdditional: Record<string, unknown> = {};
  for (const key of additionalKeys) {
    if (key === 'portfolioSections' && sectionsMirrorLegacy) continue;
    // Keine leere Bezeichnungs-Liste in ein Dokument schreiben, das nie eine hatte.
    if (
      (key === 'fondsdatenLabels' || key === 'weitereDatenLabels') &&
      origAdditional[key] === undefined &&
      Object.keys(v2Data?.[key] || {}).length === 0
    ) {
      continue;
    }
    if (v2Data?.[key] !== undefined && !deepEqual(origAdditional[key], v2Data[key])) {
      if (key === 'fondsdaten') {
        updatedAdditional[key] = ensureSquadDynamicFondsdaten(v2Data[key]);
      } else {
        updatedAdditional[key] =
          key === 'monthlyCommentary' ? v2Data.monatskommentar ?? v2Data[key] : v2Data[key];
      }
    }
  }
  // Map monatskommentar → additional_fields.monthlyCommentary
  if (
    v2Data?.monatskommentar !== undefined &&
    !deepEqual(origAdditional.monthlyCommentary, v2Data.monatskommentar)
  ) {
    updatedAdditional.monthlyCommentary = v2Data.monatskommentar;
  }
  if (v2Data?.anlagestrategie !== undefined && !deepEqual(origAdditional.anlagestrategie, v2Data.anlagestrategie)) {
    updatedAdditional.anlagestrategie = v2Data.anlagestrategie;
  }

  if (Object.keys(updatedAdditional).length) {
    payload.push({
      key: 'additional_fields',
      value: { ...origAdditional, ...updatedAdditional },
    });
  }

  return payload;
}
